import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import { getFilterDefinition } from '../../engine/filterRegistry';
import {
  FilterId,
  FilterType,
  type StaticListFilter,
} from '../../types/filters';
import { dedupeListSelections } from './instances';

/** The artifact fields this transform lands — the two that sit at the ROOT
 * rather than inside `filters.expr`. */
type RootStaticListResponse = Pick<
  TalentDiscoveryAiFilterSearchResponseV2,
  'posting_source' | 'overall_rating'
>;

/**
 * Root field → the dashboard filter it lands on. A pair, not a switch, because
 * both sides are id-only: the resolver's `id` IS the filter's option value
 * ('3' = LinkedIn, '4' = a four-star bucket), so nothing per-dimension has to
 * be decoded.
 */
const ROOT_STATIC_LISTS: ReadonlyArray<
  [keyof RootStaticListResponse, FilterId]
> = [
  ['posting_source', FilterId.POSTINGS_SOURCE],
  ['overall_rating', FilterId.OVERALL_RATING],
];

/**
 * The two "Non-TD" root fields land as STATIC_LIST filters.
 *
 * Neither lives in the expr tree, so the leaf transforms never see them —
 * before this, a resolved rating or posting source reached the page and was
 * silently ignored. That is not a missing chip: the postings read states the
 * SOURCE its figure came from and then offers a page, and a page that ignored
 * the source opened on the combined Unified feed showing a different, equally
 * real number, with nothing on screen saying so.
 *
 * An item with no `id`, OR WITH AN EMPTY ONE, is SKIPPED rather than landed.
 * The resolver writes an id only for a source or bucket it actually matched,
 * and `getPostingFilters` parses whatever id a selection carries: `parseInt('')`
 * is `NaN` and an empty string is falsy in every id comparison, so an id-less
 * chip either filters nothing or reads as the combined feed — a real source
 * nobody asked for, presented as a narrowing.
 */
export const transformRootStaticLists = ({
  response,
}: {
  response: RootStaticListResponse;
}): StaticListFilter[] => {
  const filters: StaticListFilter[] = [];

  for (const [key, filterId] of ROOT_STATIC_LISTS) {
    const selections = dedupeListSelections(
      (response[key] ?? [])
        .filter(
          (item): item is typeof item & { id: string } =>
            typeof item.id === 'string' && item.id.length > 0
        )
        // Deduped for the same reason the read dedupes ids server-side: two
        // spellings of one source are one source, and landing the same chip
        // twice would show the user a narrowing they did not ask for.
        .map((item) => ({ id: item.id, name: item.name }))
    );
    if (selections.length === 0) continue;

    const definition = getFilterDefinition(filterId);
    if (!definition || definition.type !== FilterType.STATIC_LIST) continue;

    filters.push({
      ...definition,
      selections,
      // The user asked for this in words, so it is a narrowing they chose —
      // which is what makes the chip visible and what
      // `buildCurrentPageFilterArtifact` reads back on the next turn.
      isUserSubmitted: true,
      isVisible: true,
    });
  }

  return filters;
};
